<template>
  <div
    class="p-0 bg-white shadow rounded-lg border border-slate-200 hover:shadow-md transition-all overflow-hidden relative"
    :class="{
      'cursor-pointer': !bulkSelectionMode && !isDeleting,
      'border-primary border-2': isSelected,
      'bg-blue-50': isSelected,
      'opacity-60': isDeleting,
    }"
    @click="handleItemClick"
  >
    <!-- Deletion Loading Overlay -->
    <div
      v-if="isDeleting"
      class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-lg"
    >
      <div class="flex flex-col items-center gap-2">
        <v-progress-circular indeterminate color="error" size="32" width="3" />
        <span class="text-sm font-medium text-slate-600">Deleting...</span>
      </div>
    </div>
    <!-- Selection Checkbox (visible when in bulk selection mode) -->
    <div
      v-if="bulkSelectionMode"
      class="absolute top-3 left-3 z-10"
      @click.stop
    ></div>

    <!-- Cover Photo Header (if available) -->
    <div
      v-if="coverImageUrl"
      class="w-full h-32 bg-cover bg-center relative"
      :style="{ backgroundImage: `url(${coverImageUrl})` }"
      @error="handleBackgroundImageError"
    >
      <!-- Overlay for better visual effect -->
      <div class="absolute inset-0 bg-black bg-opacity-20"></div>
    </div>

    <!-- Main Content -->
    <div class="p-4" :class="{ 'pl-12': bulkSelectionMode }">
      <!-- Row Header -->
      <div class="flex justify-between items-start mb-3">
        <div class="flex items-start gap-3 flex-grow min-w-0">
          <!-- Logo/Location Icon - positioned to overlap cover photo when available -->
          <div
            class="w-16 h-16 rounded-full bg-white flex items-center justify-center flex-shrink-0 border-2 border-white shadow-lg relative overflow-hidden"
            :class="{ '-mt-8': coverImageUrl }"
          >
            <img
              v-if="logoUrl"
              :src="logoUrl"
              :alt="member.name + ' Logo'"
              class="w-14 h-14 rounded-full object-cover"
              @error="handleImageError"
              ref="logoImage"
            />
            <div
              v-if="logoUrl && isLogoLoading"
              class="absolute inset-0 bg-slate-100 flex items-center justify-center"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
            <v-icon
              v-if="!logoUrl || logoLoadError"
              color="primary"
              :size="coverImageUrl ? 32 : 24"
              >mdi-map-marker</v-icon
            >
          </div>

          <div class="flex-grow min-w-0">
            <div class="flex items-center gap-2">
              <h2
                class="text-lg font-bold text-slate-900 truncate"
                :title="member.name"
              >
                {{ member.name }}
              </h2>
              <a
                v-if="member.placeId"
                :href="`https://www.google.com/maps/place/?q=place_id:${member.placeId}`"
                target="_blank"
                class="text-primary hover:text-primary-dark transition-colors flex-shrink-0"
                title="View on Google Maps"
                @click.stop
              >
                <v-icon small>mdi-open-in-new</v-icon>
              </a>
            </div>
            <!-- Address -->
            <p class="text-slate-500 text-sm mt-1">
              {{
                [
                  member.sub_locality,
                  member.city,
                  member.state,
                  member.zipcode,
                ].filter(Boolean).length > 0
                  ? [
                      member.sub_locality,
                      member.city,
                      member.state,
                      member.zipcode,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : member.address || "Address not available"
              }}
            </p>
          </div>
        </div>

        <div class="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
          <!-- Rating and Reviews -->
          <div class="flex items-center space-x-1">
            <span
              v-for="star_idx in 5"
              :key="'star-' + star_idx"
              class="relative inline-block text-gray-300 text-sm"
            >
              ★
              <span
                v-if="star_idx <= fullStars"
                class="absolute top-0 left-0 text-yellow-400"
              >
                ★
              </span>
              <span
                v-else-if="star_idx === fullStars + 1 && decimalPart > 0"
                class="absolute top-0 left-0 text-yellow-400 overflow-hidden"
                :style="{ width: decimalPart * 100 + '%' }"
              >
                ★
              </span>
            </span>
            <span class="text-xs text-slate-600 font-medium ml-1">
              {{ member.averageRating?.toFixed(1) }} ({{ member.reviewCount }}
              Reviews)
            </span>
            <span
              v-if="member.shallowConnect"
              class="text-xs text-slate-600 font-medium ml-3"
            >
              ({{ member.shallowMissedReviewCount || 0 }} Missed)
            </span>
          </div>

          <!-- Action Buttons - positioned below rating -->
          <div class="flex gap-1" @click.stop v-if="!bulkSelectionMode">
            <span
              v-if="!member.shallowConnect && member.created_at"
              class="text-slate-400 text-xs bg-slate-100 px-1 py-0.5 rounded-full flex items-center"
            >
            <v-icon small color="grey">mdi-link-variant</v-icon>
            {{ formatConnectionDate(member.created_at) }}
            </span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="text-slate-500 hover:text-primary"
                  @click="$emit('resend-invitation', member)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-email-send-outline</v-icon>
                </v-btn>
              </template>
              <span>Resend Invitation</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="text-slate-500 hover:text-red-600"
                  :disabled="isDeleting"
                  @click="$emit('delete', member)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete Location</span>
            </v-tooltip>
          </div>

          <!-- Bulk Selection Indicator -->
          <div v-if="bulkSelectionMode && isSelected" class="flex items-center">
            <v-chip small color="primary" text-color="white">
              <v-icon small left>mdi-check</v-icon>
              Selected
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Additional Location Details -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-slate-50 p-3 rounded-lg mb-4"
      >
        <!-- Short Address and City - 50% width -->
        <div class="grid grid-cols-2 gap-4">
          <!-- Short Address -->
          <div class="flex flex-col relative">
            <span class="text-slate-500 font-medium mb-1">Short Address</span>
            <div class="flex items-center gap-2 w-full">
              <template v-if="!isEditingShortAddress">
                <p class="text-slate-700 font-semibold truncate flex-grow">
                  {{ member.short_address || "-" }}
                </p>
                <v-icon
                  small
                  class="text-slate-400 hover:text-primary cursor-pointer flex-shrink-0"
                  @click.stop="startEditingShortAddress"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <template v-else>
                <div class="flex items-center gap-2 w-full" @click.stop>
                  <input
                    type="text"
                    v-model="editedShortAddress"
                    class="w-[calc(100%-28px)] px-2 py-1 border border-primary rounded text-slate-700 font-semibold text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    @keyup.enter="saveShortAddress"
                    @keyup.esc="cancelEditShortAddress"
                    @click.stop
                    ref="shortAddressInput"
                  />
                  <v-icon
                    small
                    color="success"
                    class="cursor-pointer flex-shrink-0"
                    @click.stop="saveShortAddress"
                  >
                    mdi-check-bold
                  </v-icon>
                </div>
              </template>
            </div>
          </div>

          <!-- City -->
          <div class="flex flex-col relative">
            <span class="text-slate-500 font-medium mb-1">City</span>
            <div class="flex items-center gap-2 w-full">
              <template v-if="!isEditingCity">
                <p class="text-slate-700 font-semibold truncate flex-grow">
                  {{ member.location_variable_city || member.city || "-" }}
                </p>
                <v-icon
                  small
                  class="text-slate-400 hover:text-primary cursor-pointer flex-shrink-0"
                  @click.stop="startEditingCity"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <template v-else>
                <div class="flex items-center gap-2 w-full" @click.stop>
                  <input
                    type="text"
                    v-model="editedCity"
                    class="w-[calc(100%-28px)] px-2 py-1 border border-primary rounded text-slate-700 font-semibold text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    @keyup.enter="saveCity"
                    @keyup.esc="cancelEditCity"
                    @click.stop
                    ref="cityInput"
                  />
                  <v-icon
                    small
                    color="success"
                    class="cursor-pointer flex-shrink-0"
                    @click.stop="saveCity"
                  >
                    mdi-check-bold
                  </v-icon>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Location Link - 50% width -->
        <div class="flex flex-col">
          <span class="text-slate-500 font-medium mb-1">Location Link</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a
                :href="locationLink"
                target="_blank"
                class="text-primary hover:underline truncate inline-flex items-center"
                @click.stop
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                {{ formatLocationLink(locationLink) }}
              </a>
            </template>
            <span class="text-wrap">{{ locationLink }}</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Data Row: Adjusted to 4 columns -->
      <div
        class="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm bg-slate-50 p-3 rounded-lg mb-4"
      >
        <div class="flex flex-col">
          <span class="text-slate-500 font-medium mb-1">Phone</span>
          <p v-if="member.phone" class="text-slate-700 font-semibold">
            {{ member.phone }}
          </p>
          <span v-else class="text-slate-700 font-semibold">-</span>
        </div>
        <div class="flex flex-col">
          <span class="text-slate-500 font-medium mb-1">Website</span>
          <a
            v-if="member.website"
            :href="member.website"
            class="text-primary hover:underline truncate"
            target="_blank"
            @click.stop
          >
            {{ formatWebsite(member.website) }}
          </a>
          <span v-else class="text-slate-700 font-semibold">-</span>
        </div>
        <div class="flex flex-col">
          <span class="text-slate-500 font-medium mb-1"
            >Profile Completion</span
          >
          <div
            v-if="member.shallowConnect && member.isProfileDataLoading"
            class="flex justify-center items-center h-full"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </div>
          <div class="flex flex-col items-start" v-else>
            <span
              class="font-semibold"
              :style="{
                color: getProfileStrengthColor(member.profile_strength),
              }"
            >
              {{ getProfileHealthText(member.profile_strength) }} ({{
                member.profile_strength || 0
              }}%)
            </span>
            <v-progress-linear
              :value="member.profile_strength || 0"
              :color="getProfileStrengthColor(member.profile_strength)"
              height="5"
              rounded
              class="mt-1"
            />
          </div>
        </div>
        <div class="flex flex-col">
          <span class="text-slate-500 font-medium mb-1">Google & Social</span>
          <div class="flex items-center gap-2 mt-0.5 flex-wrap">
            <v-icon
              v-if="member.verification_state === 'COMPLETED'"
              small
              color="success"
              title="GMB Verified"
            >
              mdi-check-circle
            </v-icon>
            <v-icon
              v-else-if="
                member.verification_state === 'PENDING' ||
                member.verification_state === 'STATE_UNSPECIFIED' ||
                member.verification_state === 'FAILED'
              "
              small
              color="amber"
              :title="`GMB ${
                member.verification_state?.toLowerCase() || 'status unknown'
              }`"
              @click="openVerificationGuide"
            >
              mdi-alert-circle-outline
            </v-icon>

            <span
              v-if="member.connectType && member.connectType === 'shallow'"
              class="text-slate-500 text-xs bg-slate-200 px-2 py-0.5 rounded-full"
            >
              Partial Access
            </span>

            <v-tooltip
              bottom
              v-if="
                member.status !== 'connected' && member.status !== 'accepted'
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="text-slate-500 hover:text-primary"
                  @click.stop="$emit('open-invitation-link', member)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-link-plus</v-icon>
                </v-btn>
              </template>
              <span>Connect Location / Resend Invite</span>
            </v-tooltip>

            <div
              v-if="
                member.status === 'connected' || member.status === 'accepted'
              "
              class="flex gap-1.5 flex-wrap items-center"
              @click.stop
            >
              <img
                v-for="platform in member.connected_apps"
                :key="platform"
                :src="platformLogoCache[platform] || ''"
                :alt="platform"
                class="w-5 h-5 object-contain"
                :title="platform"
              />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="text-slate-500 hover:text-primary"
                    @click="$emit('open-invitation-link', member)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-cog-outline</v-icon>
                  </v-btn>
                </template>
                <span>Manage Connections</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Links -->
      <div class="flex flex-wrap gap-2" @click.stop>
        <router-link
          :to="{
            path: '/profiles',
            query: {
              location: member.id,
              name: member.name,
              tab: 'all_profiles',
            },
          }"
          class="px-3 py-1.5 bg-primary bg-opacity-10 text-primary hover:bg-opacity-20 rounded text-sm font-medium transition-colors flex items-center"
        >
          <v-icon small class="mr-1">mdi-store</v-icon>
          Profile
        </router-link>

        <router-link
          :to="{
            path: '/reviews',
            query: { location: member.id, tab: 'reviews' },
          }"
          class="px-3 py-1.5 bg-primary bg-opacity-10 text-primary hover:bg-opacity-20 rounded text-sm font-medium transition-colors flex items-center"
        >
          <v-icon small class="mr-1">mdi-star</v-icon>
          Reviews
        </router-link>

        <router-link
          :to="{ path: '/performance', query: { location: member.id } }"
          class="px-3 py-1.5 bg-primary bg-opacity-10 text-primary hover:bg-opacity-20 rounded text-sm font-medium transition-colors flex items-center"
        >
          <v-icon small class="mr-1">mdi-chart-bar</v-icon>
          Stats
        </router-link>

        <router-link
          :to="{
            path: '/local-rank-insights',
            query: { location: member.id, tab: 'rank_explorer' },
          }"
          class="px-3 py-1.5 bg-primary bg-opacity-10 text-primary hover:bg-opacity-20 rounded text-sm font-medium transition-colors flex items-center"
        >
          <v-icon small class="mr-1">mdi-trophy</v-icon>
          Rank
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import ToolTip from "@/components/ui/ToolTip.vue";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/utils/firebase.utils";

export default {
  components: { ToolTip },
  props: {
    member: {
      type: Object,
      required: true,
    },
    platformLogoCache: {
      type: Object,
      default: () => ({}),
    },
    bulkSelectionMode: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLogoLoading: true,
      logoLoadError: false,
      coverLoadError: false,
      isEditingShortAddress: false,
      editedShortAddress: "",
      originalShortAddress: "",
      isEditingCity: false,
      editedCity: "",
      originalCity: "",
    };
  },
  mounted() {
    // Reset loading states when component mounts
    this.isLogoLoading = true;
    this.logoLoadError = false;
    this.coverLoadError = false;

    // Handle logo image loading
    if (this.logoUrl) {
      const img = new Image();
      img.onload = () => {
        this.isLogoLoading = false;
        this.logoLoadError = false;
      };
      img.onerror = () => {
        this.isLogoLoading = false;
        this.logoLoadError = true;
      };
      img.src = this.logoUrl;
    } else {
      this.isLogoLoading = false;
    }
  },
  watch: {
    // Watch for changes in logo URL and reset loading states
    logoUrl: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.isLogoLoading = true;
          this.logoLoadError = false;
          const img = new Image();
          img.onload = () => {
            this.isLogoLoading = false;
            this.logoLoadError = false;
          };
          img.onerror = () => {
            this.isLogoLoading = false;
            this.logoLoadError = true;
          };
          img.src = newVal;
        } else {
          this.isLogoLoading = false;
          this.logoLoadError = false;
        }
      },
    },
  },
  computed: {
    fullStars() {
      return Math.floor(this.member.averageRating);
    },
    decimalPart() {
      return this.member.averageRating - this.fullStars;
    },
    logoUrl() {
      // For deep connected locations, use GMB profile media items
      if (this.member.status === "connected" && !this.member.shallowConnect) {
        return this.member.gmbProfile?.mediaItems?.logo?.googleUrl || null;
      }

      // For shallow connected locations, use shallow profile data
      return (
        this.member.gmb_shallow_profile_data?.logo || this.member.logo || null
      );
    },
    coverImageUrl() {
      // For deep connected locations, use GMB profile media items
      if (this.member.status === "connected" && !this.member.shallowConnect) {
        return (
          this.member.gmbProfile?.mediaItems?.coverPhoto?.googleUrl || null
        );
      }

      // For shallow connected locations, use shallow profile data
      return (
        this.member.gmb_shallow_profile_data?.cover_photo ||
        this.member.cover_photo ||
        null
      );
    },
    locationLink() {
      return (
        this.member.gmbProfile?.metadata?.mapsUri ||
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          this.member.name || ""
        )}&query_place_id=${this.member.place_id || ""}`
      );
    },
  },
  methods: {
    formatWebsite(website) {
      return website?.replace(/(^\w+:|^)\/\//, "").replace(/\/$/, "") || "";
    },
    formatLocationLink(url) {
      return url?.replace(/(^\w+:|^)\/\//, "").replace(/\/$/, "") || "";
    },
    getProfileStrengthColor(strength) {
      if (strength >= 85) return "#4CAF50";
      if (strength >= 70) return "#8BC34A";
      if (strength >= 50) return "#FFA726";
      if (strength >= 30) return "#FF7043";
      return "#EF5350";
    },
    getProfileHealthText(strength) {
      if (strength >= 85) return "Excellent";
      if (strength >= 70) return "Good";
      if (strength >= 50) return "Average";
      if (strength >= 30) return "Poor";
      return "Critical";
    },
    openVerificationGuide() {
      window.open(
        "https://support.google.com/business/answer/2911778?hl=en&co=GENIE.Platform%3DDesktop",
        "_blank"
      );
    },
    handleImageError(event) {
      // If logo fails to load, hide the image element
      // The v-icon fallback will be shown instead
      event.target.style.display = "none";
    },
    handleBackgroundImageError(event) {
      // If cover photo fails to load, hide the image element
      // The v-icon fallback will be shown instead
      event.target.style.display = "none";
    },
    async startEditingShortAddress() {
      this.isEditingShortAddress = true;
      this.originalShortAddress = this.member.short_address || "";
      this.editedShortAddress = this.originalShortAddress;
      // Wait for the next tick to ensure input is rendered
      await this.$nextTick();
      this.$refs.shortAddressInput?.focus();
    },
    cancelEditShortAddress() {
      this.isEditingShortAddress = false;
      this.editedShortAddress = this.originalShortAddress;
      this.$snackbar.show({
        message: "Edit cancelled",
        color: "warning",
      });
    },
    async saveShortAddress() {
      try {
        const docRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.member.id
        );
        await updateDoc(docRef, {
          short_address: this.editedShortAddress,
        });

        // Emit an event to update the parent component's data
        this.$emit("update-short-address", {
          wid: this.member.wid,
          short_address: this.editedShortAddress,
        });

        this.isEditingShortAddress = false;
        this.originalShortAddress = this.editedShortAddress;
        this.member.short_address = this.originalShortAddress;
        this.$snackbar.show({
          message: "Short address updated successfully",
          color: "success",
        });
      } catch (error) {
        console.error("Error updating short address:", error);
        this.editedShortAddress = this.originalShortAddress;
        this.isEditingShortAddress = false;
        this.$snackbar.show({
          message: "Failed to update short address. Please try again.",
          color: "error",
        });
      }
    },
    async startEditingCity() {
      this.isEditingCity = true;
      this.originalCity =
        this.member.location_variable_city || this.member.city || "";
      this.editedCity = this.originalCity;
      // Wait for the next tick to ensure input is rendered
      await this.$nextTick();
      this.$refs.cityInput?.focus();
    },
    cancelEditCity() {
      this.isEditingCity = false;
      this.editedCity = this.originalCity;
      this.$snackbar.show({
        message: "Edit cancelled",
        color: "warning",
      });
    },
    async saveCity() {
      try {
        const docRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.member.id
        );
        await updateDoc(docRef, {
          location_variable_city: this.editedCity,
        });

        // Emit an event to update the parent component's data
        this.$emit("update-city", {
          wid: this.member.wid,
          location_variable_city: this.editedCity,
        });

        this.isEditingCity = false;
        this.originalCity = this.editedCity;
        this.member.location_variable_city = this.originalCity;
        this.$snackbar.show({
          message: "City updated successfully",
          color: "success",
        });
      } catch (error) {
        console.error("Error updating city:", error);
        this.editedCity = this.originalCity;
        this.isEditingCity = false;
        this.$snackbar.show({
          message: "Failed to update city. Please try again.",
          color: "error",
        });
      }
    },
    handleSelectionChange(value) {
      this.$emit("selection-changed", {
        member: this.member,
        selected: value,
      });
    },
    handleItemClick() {
      // Prevent clicks when location is being deleted
      if (this.isDeleting) return;

      if (this.bulkSelectionMode) {
        // In bulk selection mode, toggle selection
        this.handleSelectionChange(!this.isSelected);
      } else {
        // Normal mode, emit view-profile
        this.$emit("view-profile", this.member);
      }
    },
    formatConnectionDate(timestamp) {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.router-link-active {
  background-color: rgba(128, 80, 222, 0.2);
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease;
}
</style>
